import { Checkbox, Stack } from '@landler/component-library';
import { useTranslation } from 'react-i18next';

import { MAP_LAYER_FACTS } from '@/api/rest/resources/types/fact';
import { IndicatorProductSubscriptionConfiguration } from '@/api/rest/resources/types/IndicatorProduct';
import { WidgetSlot } from '@/api/rest/resources/types/widget';
import { FormControl, FormItem, FormLabel } from '@/components/Form';
import { useControlsContext } from '@/components/widgets/o/Form/Form';
import { OpacitySlider } from '@/components/widgets/o/Map/LayerEditMenu/OpacitySlider';

import { useInSituIndicatorConfigurationForMediaMaps } from './useInSituIndicatorConfigurationForMediaMaps';

export const MediaMapFormItems = () => {
  const { t } = useTranslation();
  const mapConfigurations = useInSituIndicatorConfigurationForMediaMaps() ?? [];
  const filteredConfigurations = mapConfigurations.filter((c) => c?.widget?.widget_format);
  if (!filteredConfigurations || filteredConfigurations.length === 0) {
    return null;
  }

  return (
    <Stack data-testid='media-map-layers' spacing={4}>
      <span className='typography-body2Semibold text-text-primary'>
        {t('shared.projects.project.heatMapLayerSections.media_maps')}
      </span>
      {filteredConfigurations.map((config) => {
        return <FormCheckboxItems key={`configuration-form-checkbox-item-${config.id}`} configuration={config} />;
      })}
    </Stack>
  );
};

const FormCheckboxItems = ({ configuration }: { configuration: IndicatorProductSubscriptionConfiguration }) => {
  const widgets = (configuration?.widget?.widget_format ?? []) as WidgetSlot[];
  return (
    <>
      {widgets.map((widget, i) => (
        <FormCheckboxItem
          key={`form-checkbox-item-${configuration.id}-${i}`}
          configuration={configuration}
          widget={widget}
        />
      ))}
    </>
  );
};

const FormCheckboxItem = ({
  configuration,
  widget,
}: {
  configuration: IndicatorProductSubscriptionConfiguration;
  widget: WidgetSlot;
}) => {
  const { setValue, watch } = useControlsContext();
  const internalLayerName = `${MAP_LAYER_FACTS.in_situ_map_layer}-${configuration?.id}`;

  const selectedLayers = watch('layers') ?? [];
  const selectedConfigurations = watch('inSituLayerConfiguration') ?? [];

  const isChecked =
    selectedConfigurations.some((c: IndicatorProductSubscriptionConfiguration) => c?.id === configuration?.id) &&
    selectedLayers.includes(internalLayerName);

  const endDateFilter = widget?.relatedData?.dateFilters?.end_date_inclusive;
  const title = widget?.relatedData?.title ?? configuration?.indicator_name;

  const handleCheckedChange = () => {
    if (isChecked) {
      setValue(
        'layers',
        selectedLayers.filter((layer: string) => layer !== internalLayerName),
      );
      setValue(
        'inSituLayerConfiguration',
        selectedConfigurations.filter((c: IndicatorProductSubscriptionConfiguration) => c?.id !== configuration?.id),
      );
      return;
    }

    setValue('layers', [...new Set([...selectedLayers, internalLayerName])]);
    setValue('inSituLayerConfiguration', [...selectedConfigurations, configuration]);
    setValue('layerOpacity', {
      ...watch('layerOpacity'),
      [`${MAP_LAYER_FACTS.in_situ_map_layer}-${configuration?.id}`]: 100,
    });

    if (endDateFilter) {
      const endYear = new Date(endDateFilter).getFullYear().toString();
      setValue('year', endYear);
    }
  };

  return (
    <FormItem className='flex flex-row gap-2'>
      <FormControl>
        <Checkbox checked={isChecked} onCheckedChange={handleCheckedChange} />
      </FormControl>
      <Stack className='justify-between w-full' direction='row'>
        <FormLabel className={` typography-body1 ${isChecked ? ' text-text-primary' : 'text-text-secondary'}`}>
          {title}
        </FormLabel>
        {isChecked && <OpacitySlider layer={internalLayerName as MAP_LAYER_FACTS} />}
      </Stack>
    </FormItem>
  );
};
